<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Tag } from 'lucide-vue-next';
import { normalizeEntityTags, resolveEntityTagColor, type EntityTagColors } from '~/lib/entityTags';

const props = withDefaults(defineProps<{
    tags?: string[];
    limit?: number;
    compact?: boolean;
    showIcon?: boolean;
    loading?: boolean;
    emptyLabel?: string;
    showEmpty?: boolean;
    colors?: EntityTagColors;
}>(), {
    tags: () => [],
    limit: 4,
    compact: false,
    showIcon: false,
    loading: false,
    emptyLabel: 'No tags',
    showEmpty: false,
    colors: () => ({}),
});

const normalizedTags = computed(() => normalizeEntityTags(props.tags));
const visibleTags = computed(() => normalizedTags.value.slice(0, Math.max(0, props.limit)));
const hiddenCount = computed(() => Math.max(0, normalizedTags.value.length - visibleTags.value.length));

function tagStyle(tag: string) {
    const color = resolveEntityTagColor(tag, props.colors);
    return {
        backgroundColor: `${color}1f`,
        borderColor: `${color}70`,
        color: 'var(--foreground)',
    };
}
</script>

<template>
    <div v-if="loading" class="flex flex-wrap gap-1.5" aria-label="Loading tags">
        <span v-for="index in 2" :key="index" class="h-5 w-16 animate-pulse rounded-full bg-muted" />
    </div>
    <div v-else-if="visibleTags.length" class="flex min-w-0 flex-wrap items-center gap-1.5">
        <Badge
            v-for="tag in visibleTags"
            :key="tag"
            variant="secondary"
            :class="['border', compact ? 'max-w-32 px-2 py-0 text-[10px] font-medium' : 'max-w-48 px-2.5 py-0.5 text-xs font-medium']"
            :style="tagStyle(tag)"
            :title="tag"
        >
            <Tag v-if="showIcon" class="!size-2.5 shrink-0" aria-hidden="true" />
            <span class="truncate">{{ tag }}</span>
        </Badge>
        <Badge v-if="hiddenCount" variant="outline" :class="compact ? 'px-2 py-0 text-[10px]' : 'px-2.5 py-0.5 text-xs'">
            +{{ hiddenCount }}
        </Badge>
    </div>
    <span v-else-if="showEmpty" class="text-xs text-muted-foreground">{{ emptyLabel }}</span>
</template>
